// Explore: the whole week, already ranked by the server (POST /picks {explore}), filtered here instantly.
// Everything in this file is pure: filters, "more like this", and the night rule (same as the picks API).
import type { Item, Pick, WeekContext } from './api'

/** Known opening hours per weekday ("sun".."sat"): [[openHour, closeHour], ...], close may pass 24. */
export type Weekly = Record<string, number[][] | boolean> & { unclear?: boolean }
export type ExploreItem = Item & { hours?: Weekly | null }
export type ExploreRow = { score: number; reasons: string[]; km: number | null; from_deck: number | null; item: ExploreItem }

// ---------- LA wall clock (mirrors supabase/functions/_shared/times.ts) ----------
const DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat']
const laFmt = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/Los_Angeles', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', weekday: 'short', hour12: false,
})
export function la(t: Date) {
  const p = Object.fromEntries(laFmt.formatToParts(t).map((x) => [x.type, x.value]))
  const hour = (Number(p.hour) % 24) + Number(p.minute) / 60
  return { dow: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(p.weekday), hour, ymd: `${p.year}-${String(p.month).padStart(2, '0')}-${String(p.day).padStart(2, '0')}` }
}
/** The instant for an LA wall-clock hour on the same LA day as `ref`, plus `addDays`. */
export function laAt(ref: Date, hour: number, addDays = 0): Date {
  const { hour: h } = la(ref)
  const t = new Date(ref.getTime() + (hour - h) * 3600e3 + addDays * 86400e3)
  const drift = la(t).hour - (((hour % 24) + 24) % 24)
  return new Date(t.getTime() - (Math.abs(drift) < 2 ? drift : 0) * 3600e3)
}

// ---------- the night rule (same as the picks API's night_ok) ----------
const DURATION_MIN: Record<string, number> = {
  comedy: 90, talks: 90, film: 120, music: 180, nightlife: 240, theater: 150, food: 90, drink: 60, art: 90,
  class: 120, market: 120, outdoors: 120, fitness: 90, community: 120, other: 120, 'day-trip': 360,
}
const EVENING_CATS = new Set(['food', 'drink', 'nightlife', 'music', 'comedy', 'theater', 'film', 'talks'])
type Window = { start: Date; end: Date }

function anchorWindow(it: ExploreItem, week: WeekContext | null | undefined, now: Date): Window | null {
  const dur = (DURATION_MIN[it.category] ?? 120) * 60e3
  if (it.kind === 'event' && it.start_at) {
    const s = new Date(it.start_at)
    const e = it.end_at ? new Date(it.end_at) : null
    if (e && e.getTime() - s.getTime() > 12 * 3600e3) return null
    if (s.getTime() < now.getTime()) return null
    return { start: s, end: e && e > s ? e : new Date(s.getTime() + dur) }
  }
  if (!EVENING_CATS.has(it.category)) return null
  const okDay = (dow: number) =>
    !(week?.days?.length && !week.days.includes(dow)) && !(week?.when === 'weekend' && ![5, 6, 0].includes(dow)) && !(week?.when === 'weeknight' && ![1, 2, 3, 4].includes(dow))
  const hours = it.hours
  if (hours && !hours.unclear) {
    for (let d = 0; d < 7; d++) {
      const day = laAt(now, 12, d)
      const dow = la(day).dow
      if (!okDay(dow)) continue
      const r = ((hours[DAY_KEYS[dow]] as number[][] | undefined) ?? []).find(([, b]) => b >= 18.5)
      if (!r) continue
      const s = laAt(day, Math.max(r[0], 18.5))
      if (s.getTime() < now.getTime() + 2 * 3600e3) continue
      return { start: s, end: new Date(Math.min(s.getTime() + dur, laAt(day, r[1]).getTime())) }
    }
    return null
  }
  for (let d = 0; d < 7; d++) {
    const s = laAt(now, 19, d)
    if (s.getTime() < now.getTime() + 2 * 3600e3) continue
    if (!okDay(la(s).dow)) continue
    return { start: s, end: new Date(s.getTime() + dur) }
  }
  return null
}

/** "Make a night of it" applies: it has coordinates and the visit ends between 4pm and 1am LA. */
export function nightOk(it: ExploreItem, week: WeekContext | null | undefined, now = new Date()): boolean {
  if (typeof it.lat !== 'number' || typeof it.lng !== 'number') return false
  const w = anchorWindow(it, week, now)
  if (!w) return false
  const { hour } = la(w.end)
  const h = la(w.end).ymd !== la(w.start).ymd ? hour + 24 : hour
  return h >= 16 && h <= 25
}

/** An Explore row as a pick, so the pick card, the map rail and the night screen can host it. */
export function asPick(row: ExploreRow, rank: number, week: WeekContext | null | undefined, now = new Date()): Pick {
  const d = row.from_deck
  return { rank, why: '', item: row.item, from_deck: d === 2 || d === 1 || d === -1 ? d : null, night_ok: nightOk(row.item, week, now) }
}

// ---------- filters ----------
export const QUICK = ['tonight', 'weekend', 'free', 'outdoors', 'active', 'late', 'near', 'radar', 'food'] as const
export type Quick = (typeof QUICK)[number]
export const CATS = ['music', 'comedy', 'art', 'film', 'outdoors', 'class', 'community', 'theater', 'market', 'fitness', 'other'] as const
export type Cat = (typeof CATS)[number]
export type Chip = Quick | `cat:${Cat}`

const FOOD = new Set(['food', 'drink'])
const NAMED = new Set<string>(CATS)

/** The category row an item sits in: food and drink only under the Food chip; anything unnamed is Other. */
export function catOf(it: Item): Cat | 'food' {
  if (FOOD.has(it.category)) return 'food'
  return NAMED.has(it.category) ? (it.category as Cat) : 'other'
}

type Facts = { tonight: boolean; weekend: boolean; late: boolean }

/** Time facts, worked out once per load against one `now` so filtering stays instant. */
export function timeFacts(it: ExploreItem, now: Date): Facts {
  const today = la(now)
  // tonight: from 4pm today (or now, if later) until 4am
  const tStart = new Date(Math.max(now.getTime(), laAt(now, 16).getTime()))
  const tEnd = laAt(now, 28)
  // this weekend: Friday 5pm to Monday 00:00; from now when it's already the weekend
  const toFri = (5 - today.dow + 7) % 7
  const inWeekend = today.dow === 0 || today.dow === 6 || (today.dow === 5 && today.hour >= 17)
  const wStart = inWeekend ? now : laAt(now, 17, toFri)
  const wEnd = laAt(now, 24, today.dow === 0 ? 0 : (7 - today.dow) % 7)
  const tags = it.tags ?? []
  let late = tags.includes('late-night')

  if (it.start_at) {
    const s = new Date(it.start_at)
    const e = it.end_at ? new Date(it.end_at) : new Date(s.getTime() + (DURATION_MIN[it.category] ?? 120) * 60e3)
    const run = e.getTime() - s.getTime() > 12 * 3600e3 // a multi-day run (an exhibition): open, but no set evening time
    const h = la(s).hour
    if (!run && (h >= 21 || h < 4)) late = true
    return {
      tonight: !run && s < tEnd && e > tStart,
      weekend: s < wEnd && e > wStart,
      late,
    }
  }
  const hours = it.hours
  if (hours && !hours.unclear) {
    const ranges = (k: number) => ((hours[DAY_KEYS[k]] as number[][] | undefined) ?? [])
    const tonight = ranges(today.dow).some(([, b]) => b >= 19 && b > today.hour)
    // the weekend days still ahead (Fri counts from the evening); today's ranges must not be over yet
    const days = !inWeekend ? [5, 6, 0] : today.dow === 5 ? [5, 6, 0] : today.dow === 6 ? [6, 0] : [0]
    const weekend = days.some((d) => ranges(d).some(([, b]) => (d === 5 && b < 19 ? false : d === today.dow ? b > today.hour : true)))
    return { tonight, weekend, late }
  }
  // no known hours and no date: doable any day, never "tonight" (we can't say it's open)
  return { tonight: false, weekend: true, late }
}

export type Indexed = { row: ExploreRow; facts: Facts; cat: Cat | 'food' }

export function indexRows(rows: ExploreRow[], now = new Date()): Indexed[] {
  return rows.map((row) => ({ row, facts: timeFacts(row.item, now), cat: catOf(row.item) }))
}

function quickMatch(x: Indexed, c: Quick): boolean {
  const it = x.row.item
  const tags = it.tags ?? []
  switch (c) {
    case 'tonight': return x.facts.tonight
    case 'weekend': return x.facts.weekend
    case 'late': return x.facts.late
    case 'free': return Boolean(it.is_free) || it.cost_tier === 'free'
    case 'outdoors': return it.category === 'outdoors' || tags.includes('outdoor')
    case 'active': return tags.includes('active') || tags.includes('hands-on') || (it.vibe?.dims?.posture ?? 0) > 0.4
    case 'near': return x.row.km !== null && x.row.km <= 5
    case 'radar': return it.iykyk_score >= 0.65
    case 'food': return x.cat === 'food'
  }
}

/**
 * Time chips (Tonight, This weekend) are either-or; the other quick chips must all hold; categories are either-or.
 * Food is a category here: off, food and drink stay out; on, they're in (alone: only food).
 */
export function applyFilters(rows: Indexed[], chips: Chip[]): Indexed[] {
  const on = new Set(chips)
  const times = (['tonight', 'weekend'] as const).filter((c) => on.has(c))
  const musts = (['free', 'outdoors', 'active', 'late', 'near', 'radar'] as const).filter((c) => on.has(c))
  const cats = new Set<string>(chips.filter((c) => c.startsWith('cat:')).map((c) => c.slice(4)))
  if (on.has('food')) cats.add('food')
  return rows.filter((x) => {
    if (cats.size ? !cats.has(x.cat) : x.cat === 'food') return false
    if (times.length && !times.some((c) => quickMatch(x, c))) return false
    return musts.every((c) => quickMatch(x, c))
  })
}

// ---------- more like this ----------
const norm = (s: string | null | undefined) => (s ?? '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim()

function sameVenue(a: Item, b: Item): boolean {
  const va = norm(a.venue_name)
  if (va && va === norm(b.venue_name)) return true
  if (typeof a.lat === 'number' && typeof b.lat === 'number' && typeof a.lng === 'number' && typeof b.lng === 'number') {
    return Math.abs(a.lat - b.lat) < 0.0004 && Math.abs(a.lng - b.lng) < 0.0004
  }
  return false
}

function vibeCloseness(a: Item, b: Item): number {
  const da = a.vibe?.dims
  const db = b.vibe?.dims
  if (!da || !db) return 0
  let sum = 0
  let n = 0
  for (const k of Object.keys(da)) {
    if (typeof db[k] !== 'number' || typeof da[k] !== 'number') continue
    sum += Math.abs(da[k] - db[k])
    n++
  }
  return n ? 1 - sum / n / 2 : 0
}

/** Tags too common to say two things are alike. */
const WEAK = new Set(['indoor', 'social', 'classic', 'crowd-pleaser', 'solo-friendly', 'free'])

/**
 * The most similar things on this week's list: shared tags, same category and a close vibe; never the same venue,
 * never two from one venue, never the same title. Food only when the source is food. Better fits break ties.
 */
export function similar(source: Item, rows: ExploreRow[], n = 6): ExploreRow[] {
  const tags = new Set((source.tags ?? []).filter((t) => !WEAK.has(t)))
  const srcFood = FOOD.has(source.category)
  const title = norm(source.title)
  const scored: { row: ExploreRow; s: number }[] = []
  for (const row of rows) {
    const it = row.item
    if (it.id === source.id || norm(it.title) === title || sameVenue(it, source)) continue
    if (FOOD.has(it.category) && !srcFood) continue
    const shared = (it.tags ?? []).filter((t) => tags.has(t)).length
    const s = (it.category === source.category ? 1 : 0) + 0.35 * Math.min(4, shared) + 1.2 * vibeCloseness(source, it) + 0.3 * row.score
    scored.push({ row, s })
  }
  scored.sort((a, b) => b.s - a.s)
  const out: ExploreRow[] = []
  for (const { row } of scored) {
    if (out.some((o) => sameVenue(o.item, row.item))) continue
    out.push(row)
    if (out.length >= n) break
  }
  return out
}

/** Has usable coordinates (a pin on the map). */
export const located = (i: Item) => typeof i.lat === 'number' && typeof i.lng === 'number' && Number.isFinite(i.lat) && Number.isFinite(i.lng)

/** km -> miles, one decimal (US readers). */
export const miles = (km: number) => Math.round(km * 0.621371 * 10) / 10
